import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  FileText, Plus, Trash2, Search, BookOpen, Shield, Save,
  Layers, ArrowLeft, Copy, Download
} from "lucide-react";

const NotesPage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState({ builtin: [], custom: [] });
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [searchQ, setSearchQ] = useState("");

  // Editor state
  const [activeNote, setActiveNote] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [editorTitle, setEditorTitle] = useState("");
  const [sectionsData, setSectionsData] = useState({});
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const debounceRef = useRef(null);

  // Dialogs
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", description: "", sections: [{ _uid: "s_1", key: "section_1", label: "Section 1", placeholder: "" }] });
  const [creatingTpl, setCreatingTpl] = useState(false);

  // Initial load
  useEffect(() => { fetchTemplates(); fetchNotes(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/notes/templates`);
      setTemplates(res.data);
    } catch (err) {
      console.error("[NotesPage] fetchTemplates failed", err);
    }
  };

  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/notes`);
      setNotes(res.data);
    } catch { toast.error("Failed to load notes"); }
    finally { setLoadingNotes(false); }
  };

  const startNoteFromTemplate = async (template) => {
    setShowTemplatePicker(false);
    // Initialize section data with empty strings
    const initialData = {};
    template.sections.forEach(s => { initialData[s.key] = ""; });
    try {
      const res = await axios.post(`${API}/notes`, {
        template_id: template.template_id,
        title: template.name,
        sections_data: initialData,
      });
      setNotes(prev => [res.data, ...prev]);
      setActiveNote(res.data);
      setActiveTemplate(template);
      setEditorTitle(res.data.title);
      setSectionsData(initialData);
    } catch { toast.error("Failed to start note"); }
  };

  const startBlankNote = async () => {
    const initialData = { content: "" };
    try {
      const res = await axios.post(`${API}/notes`, {
        title: "Untitled note",
        sections_data: initialData,
      });
      setNotes(prev => [res.data, ...prev]);
      setActiveNote(res.data);
      setActiveTemplate({ template_id: null, name: "Free-form note", sections: [{ key: "content", label: "Content", placeholder: "Start writing..." }] });
      setEditorTitle(res.data.title);
      setSectionsData(initialData);
    } catch { toast.error("Failed to create note"); }
  };

  const openNote = async (note) => {
    try {
      const res = await axios.get(`${API}/notes/${note.note_id}`);
      setActiveNote(res.data);
      setEditorTitle(res.data.title);
      setSectionsData(res.data.sections_data || {});
      // Resolve template
      if (res.data.template_id) {
        try {
          const t = await axios.get(`${API}/notes/templates/${res.data.template_id}`);
          setActiveTemplate(t.data);
        } catch {
          // Fallback: build sections from existing data keys
          setActiveTemplate({
            template_id: res.data.template_id,
            name: res.data.template_name || "Note",
            sections: Object.keys(res.data.sections_data || {}).map(k => ({ key: k, label: k.replace(/_/g, " "), placeholder: "" })),
          });
        }
      } else {
        setActiveTemplate({ template_id: null, name: "Free-form note", sections: Object.keys(res.data.sections_data || { content: "" }).map(k => ({ key: k, label: k === "content" ? "Content" : k.replace(/_/g, " "), placeholder: "" })) });
      }
      setSaveStatus("idle");
    } catch { toast.error("Failed to open note"); }
  };

  // Auto-save with 1.2s debounce
  const triggerSave = useCallback((title, data) => {
    if (!activeNote) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus("saving");
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.patch(`${API}/notes/${activeNote.note_id}`, {
          title, sections_data: data,
        });
        setSaveStatus("saved");
        setNotes(prev => prev.map(n => n.note_id === res.data.note_id ? res.data : n).sort((a, b) => b.updated_at.localeCompare(a.updated_at)));
        setActiveNote(res.data);
      } catch {
        setSaveStatus("error");
      }
    }, 1200);
  }, [activeNote]);

  const handleTitleChange = (v) => {
    setEditorTitle(v);
    triggerSave(v, sectionsData);
  };

  const handleSectionChange = (key, v) => {
    const newData = { ...sectionsData, [key]: v };
    setSectionsData(newData);
    triggerSave(editorTitle, newData);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success("Note deleted");
      setNotes(prev => prev.filter(n => n.note_id !== noteId));
      if (activeNote?.note_id === noteId) {
        setActiveNote(null);
        setActiveTemplate(null);
      }
    } catch { toast.error("Failed"); }
  };

  // Custom template create
  const addTplSection = () => {
    setTplForm(f => ({
      ...f,
      sections: [...f.sections, { _uid: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, key: `section_${f.sections.length + 1}`, label: `Section ${f.sections.length + 1}`, placeholder: "" }]
    }));
  };
  const updateTplSection = (idx, field, value) => {
    setTplForm(f => ({
      ...f,
      sections: f.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };
  const removeTplSection = (idx) => {
    setTplForm(f => ({ ...f, sections: f.sections.filter((_, i) => i !== idx) }));
  };

  const createTemplate = async () => {
    if (!tplForm.name.trim()) { toast.error("Template name required"); return; }
    if (tplForm.sections.length === 0) { toast.error("At least one section required"); return; }
    setCreatingTpl(true);
    try {
      await axios.post(`${API}/notes/templates`, tplForm);
      toast.success("Template saved!");
      setShowCreateTemplate(false);
      setTplForm({ name: "", description: "", sections: [{ _uid: "s_1", key: "section_1", label: "Section 1", placeholder: "" }] });
      fetchTemplates();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setCreatingTpl(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/notes/templates/${tpl.template_id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const cloneTemplate = async (tpl) => {
    try {
      toast.loading("Cloning to your account…", { id: `clone-${tpl.template_id}` });
      const res = await axios.post(`${API}/notes/templates/${tpl.template_id}/clone`);
      toast.success(`"${res.data.name}" added to My templates — edit freely!`, { id: `clone-${tpl.template_id}` });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Clone failed", { id: `clone-${tpl.template_id}` });
    }
  };

  const downloadTemplate = async (tpl) => {
    try {
      toast.loading(`Building ${tpl.name} PDF…`, { id: `dl-${tpl.template_id}` });
      const res = await axios.get(`${API}/notes/templates/${tpl.template_id}/download`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const safeName = tpl.name.replace(/[^a-z0-9]/gi, "_").slice(0, 60);
      a.href = blobUrl;
      a.download = `DHDH_Template_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success("Downloaded", { id: `dl-${tpl.template_id}` });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Download failed", { id: `dl-${tpl.template_id}` });
    }
  };

  const filteredNotes = notes.filter(n =>
    !searchQ || n.title.toLowerCase().includes(searchQ.toLowerCase()) ||
    Object.values(n.sections_data || {}).some(v => v?.toLowerCase().includes(searchQ.toLowerCase()))
  );

  const formatRelative = (iso) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(iso).toLocaleDateString("en-CA");
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8" data-testid="notes-page">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-7rem)]">
          {/* Sidebar */}
          <aside className="bg-white border border-[#E5DFEC] rounded-2xl flex flex-col overflow-hidden">
            <div className="p-4 border-b border-[#E5DFEC] shrink-0">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-[#6B4E8A]" />
                <h1 className="font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <EditableText blockKey="notes.title" fallback="My Notes" as="span" />
                </h1>
              </div>
              <Button onClick={() => setShowTemplatePicker(true)} className="w-full bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" data-testid="new-note-btn">
                <Plus className="w-4 h-4 mr-2" />New Note
              </Button>
              <div className="relative mt-3">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search notes..." className="pl-8 h-9 text-sm bg-[#FAF7F2] border-[#E5DFEC]" data-testid="search-notes" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loadingNotes ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-[#F0ECF7] rounded-xl animate-pulse" />)}</div>
              ) : filteredNotes.length > 0 ? (
                <div className="space-y-1">
                  {filteredNotes.map(n => (
                    <button
                      key={n.note_id}
                      onClick={() => openNote(n)}
                      className={`w-full text-left p-3 rounded-xl transition-colors ${activeNote?.note_id === n.note_id ? "bg-[#6B4E8A] text-white" : "hover:bg-[#F0ECF7]"}`}
                      data-testid={`note-row-${n.note_id}`}
                    >
                      <p className={`text-sm font-medium truncate ${activeNote?.note_id === n.note_id ? "text-white" : "text-[#6B4E8A]"}`}>{n.title}</p>
                      <p className={`text-[11px] truncate ${activeNote?.note_id === n.note_id ? "text-white/60" : "text-[#94A3B8]"}`}>
                        {n.template_name || "Free-form"} · {formatRelative(n.updated_at)}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#94A3B8] text-center py-8">{searchQ ? "No notes match" : "No notes yet — start one!"}</p>
              )}
            </div>
            <div className="p-3 border-t border-[#E5DFEC] shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setShowCreateTemplate(true)} className="w-full justify-start text-[#64748B] hover:text-[#6B4E8A]" data-testid="manage-templates-btn">
                <Layers className="w-4 h-4 mr-2" />Create custom template
              </Button>
            </div>
          </aside>

          {/* Editor */}
          <main className="bg-white border border-[#E5DFEC] rounded-2xl flex flex-col overflow-hidden">
            {activeNote && activeTemplate ? (
              <>
                <div className="border-b border-[#E5DFEC] p-4 flex items-center gap-3 shrink-0">
                  <button onClick={() => { setActiveNote(null); setActiveTemplate(null); }} className="lg:hidden p-1.5 hover:bg-[#F0ECF7] rounded-lg" data-testid="editor-back-btn"><ArrowLeft className="w-4 h-4" /></button>
                  <div className="flex-1 min-w-0">
                    <Input
                      value={editorTitle}
                      onChange={e => handleTitleChange(e.target.value)}
                      className="border-0 shadow-none focus-visible:ring-0 px-0 text-lg font-medium h-auto"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                      placeholder="Note title"
                      data-testid="editor-title"
                    />
                    <p className="text-xs text-[#94A3B8] flex items-center gap-1.5 mt-0.5">
                      <BookOpen className="w-3 h-3" />{activeTemplate.name}
                      {activeTemplate.regulator && <span className="px-1.5 py-0.5 rounded-full bg-[#8DB5A0]/15 text-[#8DB5A0] text-[10px] inline-flex items-center gap-1"><Shield className="w-2.5 h-2.5" />{activeTemplate.regulator}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs flex items-center gap-1 ${saveStatus === "saving" ? "text-[#D97706]" : saveStatus === "saved" ? "text-[#10B981]" : saveStatus === "error" ? "text-[#EF4444]" : "text-[#94A3B8]"}`} data-testid="save-status">
                      <Save className="w-3 h-3" />
                      {saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Save failed" : "Auto-save on"}
                    </span>
                    <button onClick={() => handleDeleteNote(activeNote.note_id)} className="p-2 hover:bg-[#EF4444]/10 rounded-lg" data-testid="delete-note-btn"><Trash2 className="w-4 h-4 text-[#EF4444]" /></button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                  {activeTemplate.sections.map(section => (
                    <div key={section.key}>
                      <Label className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">{section.label}</Label>
                      <Textarea
                        value={sectionsData[section.key] || ""}
                        onChange={e => handleSectionChange(section.key, e.target.value)}
                        placeholder={section.placeholder}
                        className="mt-2 min-h-[120px] bg-[#FAF7F2] border-[#E5DFEC] resize-y"
                        data-testid={`section-${section.key}`}
                      />
                    </div>
                  ))}
                  <div className="bg-[#FFF7ED] border border-[#FDBA74] rounded-xl p-3 flex items-start gap-2 mt-6">
                    <Shield className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" />
                    <p className="text-xs text-[#9A3412]">
                      <strong>Reminder:</strong> Notes are private to you. Avoid storing patient-identifying information unless your practice's privacy and PIPEDA / provincial data-protection requirements allow it. Use only the minimum necessary information.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-[#F0ECF7] flex items-center justify-center mb-4"><FileText className="w-8 h-8 text-[#8DB5A0]" /></div>
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Select a note or start a new one</h3>
                <p className="text-sm text-[#64748B] max-w-sm mb-4">Pick from regulator-aligned templates (SOAP, Periodontal Assessment, Recall, Treatment Plan) or build your own.</p>
                <Button onClick={() => setShowTemplatePicker(true)} className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"><Plus className="w-4 h-4 mr-2" />New Note</Button>
              </div>
            )}
          </main>
        </div>

        {/* Template picker dialog */}
        <Dialog open={showTemplatePicker} onOpenChange={setShowTemplatePicker}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-picker-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Pick a template</DialogTitle>
              <DialogDescription>Start from a regulator-aligned template or use a free-form note.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <button onClick={startBlankNote} className="w-full text-left border border-dashed border-[#E5DFEC] rounded-xl p-4 hover:border-[#8DB5A0] hover:bg-[#FAF7F2] transition-colors" data-testid="template-blank">
                <p className="font-medium text-[#6B4E8A]">Free-form note</p>
                <p className="text-xs text-[#64748B]">A blank canvas — write whatever you need.</p>
              </button>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Built-in templates</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {templates.builtin.map(t => (
                    <div
                      key={t.template_id}
                      className="text-left border border-[#E5DFEC] rounded-xl p-3 hover:border-[#6B4E8A] transition-colors group"
                      data-testid={`template-${t.template_id}`}
                    >
                      <button onClick={() => startNoteFromTemplate(t)} className="text-left w-full">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-medium text-[#6B4E8A] text-sm">{t.name}</p>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8DB5A0]/15 text-[#8DB5A0] inline-flex items-center gap-1 shrink-0"><Shield className="w-2.5 h-2.5" />{t.regulator}</span>
                        </div>
                        <p className="text-xs text-[#64748B] line-clamp-2">{t.description}</p>
                        <p className="text-[10px] text-[#94A3B8] mt-1">{t.sections.length} sections</p>
                      </button>
                      <div className="flex gap-2 mt-2 pt-2 border-t border-[#E5DFEC]">
                        <button
                          onClick={(e) => { e.stopPropagation(); cloneTemplate(t); }}
                          className="text-[10px] text-[#6B4E8A] hover:underline inline-flex items-center gap-1"
                          data-testid={`clone-template-${t.template_id}`}
                          title="Make an editable copy in your account"
                        >
                          <Copy className="w-3 h-3" /> Clone & edit
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadTemplate(t); }}
                          className="text-[10px] text-[#8DB5A0] hover:underline inline-flex items-center gap-1"
                          data-testid={`download-template-${t.template_id}`}
                          title="Download this template as PDF"
                        >
                          <Download className="w-3 h-3" /> Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {templates.custom.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">My templates</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {templates.custom.map(t => (
                      <div key={t.template_id} className="border border-[#E5DFEC] rounded-xl p-3 hover:border-[#6B4E8A] transition-colors group">
                        <button onClick={() => startNoteFromTemplate(t)} className="text-left w-full" data-testid={`template-${t.template_id}`}>
                          <p className="font-medium text-[#6B4E8A] text-sm">{t.name}</p>
                          {t.description && <p className="text-xs text-[#64748B] line-clamp-2">{t.description}</p>}
                          <p className="text-[10px] text-[#94A3B8] mt-1">{t.sections.length} sections</p>
                        </button>
                        <div className="flex gap-2 mt-2 pt-2 border-t border-[#E5DFEC]">
                          <button onClick={(e) => { e.stopPropagation(); downloadTemplate(t); }} className="text-[10px] text-[#8DB5A0] hover:underline inline-flex items-center gap-1" data-testid={`download-template-${t.template_id}`}>
                            <Download className="w-3 h-3" /> PDF
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }} className="text-[10px] text-[#EF4444] hover:underline ml-auto">Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button variant="outline" onClick={() => { setShowTemplatePicker(false); setShowCreateTemplate(true); }} className="w-full rounded-full" data-testid="open-create-template">
                <Layers className="w-4 h-4 mr-2" />Create a custom template
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create template dialog */}
        <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-template-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>New custom template</DialogTitle>
              <DialogDescription>Define your own sections. You can use this template anytime to start a new note.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label>Template name</Label>
                <Input value={tplForm.name} onChange={e => setTplForm(f => ({ ...f, name: e.target.value }))} maxLength={80} placeholder="e.g. My Daily Wrap-up" className="mt-1.5" data-testid="tpl-name" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={tplForm.description} onChange={e => setTplForm(f => ({ ...f, description: e.target.value }))} maxLength={300} placeholder="Optional" className="mt-1.5" />
              </div>
              <div>
                <Label className="flex items-center justify-between">
                  Sections
                  <Button type="button" size="sm" variant="ghost" onClick={addTplSection} className="text-[#8DB5A0] h-7"><Plus className="w-3.5 h-3.5 mr-1" />Add section</Button>
                </Label>
                <div className="space-y-2 mt-2">
                  {tplForm.sections.map((s, i) => (
                    <div key={s._uid || `legacy-${i}`} className="border border-[#E5DFEC] rounded-xl p-3 space-y-2" data-testid={`tpl-section-${i}`}>
                      <div className="flex items-center gap-2">
                        <Input value={s.label} onChange={e => updateTplSection(i, "label", e.target.value)} placeholder="Section label" className="flex-1 h-8 text-sm" />
                        <button onClick={() => removeTplSection(i)} className="p-1.5 hover:bg-[#EF4444]/10 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-[#EF4444]" /></button>
                      </div>
                      <Input value={s.key} onChange={e => updateTplSection(i, "key", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} placeholder="key (lowercase, underscores)" className="h-8 text-xs font-mono text-[#64748B]" />
                      <Input value={s.placeholder} onChange={e => updateTplSection(i, "placeholder", e.target.value)} placeholder="Placeholder hint (optional)" className="h-8 text-sm" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreateTemplate(false)} className="rounded-full">Cancel</Button>
                <Button onClick={createTemplate} disabled={creatingTpl || !tplForm.name.trim()} className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="submit-create-template">
                  {creatingTpl ? "Saving..." : "Save template"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default NotesPage;
